import React from "react";
import styled from "styled-components";
import MyHeader from "../../components/common/MyHeader";
import { useNavigate } from "react-router-dom";
import { FaGithub, FaFigma, FaPlus, FaUserCog, FaDiscord } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";

function ConnectionPage() {
  const navigate = useNavigate();
  const { groupMembers } = useAuth();

  const handleGitHubSetup = () => {
    const allFilled = groupMembers?.every(m => m.github_username);
    const path = allFilled ? "/repo-setup" : "/group-setup";
    navigate(path);
  };

  const connections = [
    {
      name: "GitHub Setup",
      description: "Connect your repository and manage your team’s GitHub link.",
      icon: <FaGithub />,
      color: "#171515",
      route: "/repo-setup",
    },
    {
      name: "Discord Setup",
      description: "Add your Discord username so your real name shows in admin view.",
      icon: <FaDiscord />,
      color: "#5865F2",
      route: "/discord-setup",
    },
    {
      name: "Figma Setup",
      description: "Authorize your Figma account for collaborative design access.",
      icon: <FaFigma />,
      color: "#F24E1E",
      route: "/figma-setup",
    },
    {
      name: "Login & Account Setup",
      description: "Connect or disconnect Google and email providers.",
      icon: <FaUserCog />,       
      color: "#2563EB",
      route: "/auth-setup",
    },
    {
      name: "More Integrations",
      description: "Upcoming integrations will appear here.",
      icon: <FaPlus />,
      color: "#6B7280",
      route: null,
    },
  ];

  return (
    <>
      <MyHeader />
      <PageWrapper>
        <Container>
          <Header>
            <Title>Team Connections</Title>
            <Subtitle>
              Manage all your integration setups in one place.
            </Subtitle>
          </Header>

          <CardGrid>
            {connections.map((conn) => {
              const handleClick =
                conn.name === "GitHub Setup"
                  ? handleGitHubSetup
                  : conn.route
                    ? () => navigate(conn.route)
                    : undefined;

              return (
                <ConnectionCard
                  key={conn.name}
                  onClick={handleClick}
                  disabled={!conn.route && conn.name !== "GitHub Setup"}
                >
                  <IconWrapper style={{ color: conn.color }}>{conn.icon}</IconWrapper>
                  <CardContent>
                    <CardTitle>{conn.name}</CardTitle>
                    <CardDesc>{conn.description}</CardDesc>
                  </CardContent>
                </ConnectionCard>
              );
            })}
          </CardGrid>
        </Container>
      </PageWrapper>
    </>
  );
}

export default ConnectionPage;

/* ----- 🎨 Styles ----- */
const PageWrapper = styled.div`
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 2rem 1.5rem;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  color: #6b7280;
`;

const CardGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const ConnectionCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconWrapper = styled.div`
  font-size: 1.8rem;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  flex: 1;
`;

const CardTitle = styled.h3`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const CardDesc = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
`;
